import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSlack } from "@/contexts/SlackContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { Bug, ListChecks, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function FloatingButtons() {
  const { user } = useAuth();
  const { unreadCount } = useSlack();
  const { hasFloating, hasPerm } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [slackLinked, setSlackLinked] = useState(null);

  useEffect(() => {
    if (user) api.get("/slack/me").then(r => setSlackLinked(r.data?.linked)).catch(() => {});
  }, [user]);

  if (!user) return null;
  if (location.pathname === "/login") return null;

  const showSlack = hasFloating("slack");
  const showTasks = hasFloating("tasks");
  const showBugs = hasFloating("bugs");
  const showMyBoard = hasPerm("task_board", "view");

  if (!showSlack && !showTasks && !showBugs && !showMyBoard) return null;

  const handleSlackClick = async () => {
    if (slackLinked === false) {
      // Not linked — prompt to connect
      try {
        const r = await api.get("/slack/oauth/initiate");
        const wsName = r.data.workspace_name || "";
        if (wsName) toast.info(`Workspace: ${wsName}.slack.com — use this if Slack asks`, { duration: 8000 });
        const popup = window.open(r.data.url, "slack_oauth", "width=600,height=700");
        const check = setInterval(() => {
          if (popup && popup.closed) {
            clearInterval(check);
            api.get("/slack/me").then(r => { setSlackLinked(r.data?.linked); if (r.data?.linked) { toast.success("Slack linked! Opening channel..."); navigate("/slack"); } }).catch(() => {});
          }
        }, 1000);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to initiate Slack link"); }
    } else {
      navigate("/slack");
    }
  };

  return (
    <div className="fixed right-6 top-1/2 -translate-y-1/2 z-[60] flex flex-col gap-2 items-end" data-testid="floating-buttons">
      {/* Slack button */}
      {showSlack && (
        <button
          className="relative flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#4A154B] hover:bg-[#3b1139] text-white shadow-lg hover:shadow-xl transition-all duration-200 text-sm font-medium"
          onClick={handleSlackClick}
          data-testid="floating-slack-btn"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.27 0a2.528 2.528 0 0 1-2.522 2.521 2.528 2.528 0 0 1-2.52-2.521V2.522A2.528 2.528 0 0 1 15.165 0a2.528 2.528 0 0 1 2.521 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.521 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.27a2.527 2.527 0 0 1-2.52-2.522 2.527 2.527 0 0 1 2.52-2.52h6.313A2.528 2.528 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.521h-6.313z"/></svg>
          {slackLinked === false ? "Connect Slack" : "Slack"}
          {unreadCount > 0 && slackLinked && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 ring-2 ring-white" data-testid="floating-slack-unread">{unreadCount > 99 ? "99+" : unreadCount}</span>
          )}
        </button>
      )}

      {/* My Task Board — developer/tester */}
      {showMyBoard && !showTasks && (
        <button
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 text-sm font-medium"
          onClick={() => navigate("/task-board")}
          data-testid="floating-myboard-btn"
        >
          <ClipboardList className="w-4 h-4" />My Board
        </button>
      )}

      {/* On Board Tasks — manager/admin */}
      {showTasks && (
        <button
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 text-sm font-medium"
          onClick={() => navigate("/on-board?tab=tasks")}
          data-testid="floating-tasks-btn"
        >
          <ListChecks className="w-4 h-4" />Tasks
        </button>
      )}

      {/* Bugs Management */}
      {showBugs && (
        <button
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 text-sm font-medium"
          onClick={() => navigate("/bugs")}
          data-testid="floating-bugs-btn"
        >
          <Bug className="w-4 h-4" />Bugs
        </button>
      )}
    </div>
  );
}
